import React, { FC, useContext, useMemo } from 'react';

import {
  CollapsedLeftLogoIcon,
  H3,
  Separator,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { SettingsFrame } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';

import { ClientLogo } from './ClientLogo';
import { IShellIconProps } from './interfaces';
import s from './ShellIcon.module.scss';

export const ShellIcon: FC<IShellIconProps> = ({
  iconLink,
  additionalColor,
  setState,
}) => {
  const { t } = useContext(LocalizationContext);

  const changeIconLink = (value: string): void => {
    setState((prev) => ({ ...prev, shellSkinLogo: value }));
  };

  const styleAdditionalColor = useMemo(
    () => ({
      background: `linear-gradient(0deg, ${additionalColor}3D, ${additionalColor}3D),
linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))`,
    }),
    [additionalColor]
  );

  const isAvailable = useIsFeatureAvailable('shell_skin');

  return (
    <SettingsFrame>
      <div className={s.container}>
        <div className={s.header}>
          <div className={s.info}>
            <H3>{t('settings.shell.customization.logo.title')}</H3>
            <Tooltip
              contentClassName={s.tooltip_content}
              position="bottom-left"
              content={
                <div className={s.tooltip_frame}>
                  <H3>{t('setting.shell.logo.title')}</H3>
                  <ul className={s.tooltip_list}>
                    <li className={s.tooltip_item}>
                      <span className={s.tooltip_text}>
                        {t('setting.shell.bg.optimal')}
                      </span>
                      <span className={s.tooltip_value}>96x96</span>
                    </li>
                    <li className={s.tooltip_item}>
                      <span className={s.tooltip_text}>
                        {t('application.image.maxSize')}
                      </span>
                      <span className={s.tooltip_value}>
                        500 {t('setting.shell.logo.optimal.size')}
                      </span>
                    </li>
                    <li className={s.tooltip_item}>
                      <span className={s.tooltip_text}>
                        {t('application.image.format')}
                      </span>
                      <span className={s.tooltip_value}>.svg, .png</span>
                    </li>
                  </ul>
                </div>
              }
            />
          </div>
          <span className={s.subtitle}>
            {t('settings.shell.customization.logo.description')}
          </span>
        </div>
        <Separator />
        <div className={s.images}>
          <button
            className={cn(
              s.logo_default,
              !iconLink && s.selected,
              !isAvailable && s.disabled
            )}
            style={styleAdditionalColor}
            onClick={() => changeIconLink('')}
            disabled={!isAvailable}
          >
            <div className={s.circle} />
            <CollapsedLeftLogoIcon color="light" />
          </button>
          <ClientLogo
            imageLink={iconLink}
            onUpload={changeIconLink}
            style={styleAdditionalColor}
            disabled={!isAvailable}
          />
        </div>
      </div>
    </SettingsFrame>
  );
};
